import { useEffect, useRef, useState,useMemo } from "react";
import { Table, Input, Select, Layout, ConfigProvider, Flex, TableColumnsType, Button, DatePicker } from "antd";
import { TablePaginationConfig, TableProps } from "antd/es/table";
import { SorterResult } from "antd/es/table/interface";
import dayjs from "dayjs";
import { legalSearch,getClients,getCaseTypeOptions,getStatusOptions } from "../../../service/httpsCalls";
import { Value } from "sass";
import debounce from 'lodash.debounce';

const { Content } = Layout;
const { RangePicker } = DatePicker;

const searchTypeOptions = [
  { label: "All", value: "all" },
  { label: "Member Id", value: "member-id" },
  { label: "Member Name", value: "member-name" },
  { label: "Claim Number", value: "claim-number" },
  { label: "Provider", value: "provider" },
];

export interface SearchParams {
  pageIndex: number;
  pageSize: number;
  sortField: string;
  sortDirection: string;
  receivedDateFrom: string | null;
  receivedDateTo: string | null;
  incidentDateFrom: string | null;
  incidentDateTo: string | null;
  searchText: string;
  searchType: string;
  statusId: number[] | null;
  caseTypeId: number[] | null;
  clientIds: number[] | null;
}

export default function Legal() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<any>>({});
  const listRef = useRef<HTMLDivElement>(null);
  const [searchTypeValue, setSearchTypeValue] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: boolean }>({});
  const [statusFilter, setStatusFilter] = useState<number[] | null>(null);
  const [caseTypeFilter, setCaseTypeFilter] = useState<number[] | null>(null);
  const [clientFilter, setClientFilter] = useState<number[] | null>(null);
  const [statusOptions, setStatusOptions] = useState([]);
  const [caseTypeOptions, setCaseTypeOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const baseUrl = window.localStorage.getItem("Referrer");

  const useDebounce = (callback: any) => {
    const ref = useRef<any>();

    useEffect(() => {
        ref.current = callback;
    }, [callback]);

    const debouncedCallback = useMemo(() => {
        const func = () => {
            ref.current?.();
        };

        return debounce(func, 1000);
    }, []);

    return debouncedCallback;
};


const debouncedSearch = useDebounce(() => {
    const params: SearchParams = {
        pageIndex: currentPage - 1,
        pageSize: pageSize,
        sortField: sortedInfo.column ? sortedInfo.field?.toString() || "" : "",
        sortDirection: sortedInfo.order ? (sortedInfo.order === "ascend" ? "ascend" : "descend") : "",
        receivedDateFrom: appliedDateFilters.receivedDateFrom,
        receivedDateTo: appliedDateFilters.receivedDateTo,
        incidentDateFrom: appliedDateFilters.incidentDateFrom,
        incidentDateTo: appliedDateFilters.incidentDateTo,
        searchText: searchText,
        searchType: searchTypeValue,
        statusId: statusFilter,
        caseTypeId: caseTypeFilter,
        clientIds: clientFilter,
    };

    loadData(params);
});

  // Stores selected but not applied dates
  const [tempDateFilters, setTempDateFilters] = useState<{
    receivedDate: [dayjs.Dayjs | null, dayjs.Dayjs | null];
    incidentDate: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  }>({
    receivedDate: [null, null],
    incidentDate: [null, null],
  });

  // Stores final applied date filters
  const [appliedDateFilters, setAppliedDateFilters] = useState({
    receivedDateFrom: null,
    receivedDateTo: null,
    incidentDateFrom: null,
    incidentDateTo: null,
  });

  // Called when incident date filter opens/closes
const handleIncidentDateFilterOpen = (visible: boolean) => {
  if (visible) {
    const from = appliedDateFilters.incidentDateFrom
      ? dayjs(appliedDateFilters.incidentDateFrom)
      : null;
    const to = appliedDateFilters.incidentDateTo
      ? dayjs(appliedDateFilters.incidentDateTo)
      : null;

    setTempDateFilters((prev) => ({
      ...prev,
      incidentDate: [from, to],
    }));
  }
};

const handleRecievedDateFilterOpen = (visible:boolean) => {
  if (visible) {
    const from = appliedDateFilters.receivedDateFrom
      ? dayjs(appliedDateFilters.receivedDateFrom)
      : null;
    const to = appliedDateFilters.receivedDateTo
      ? dayjs(appliedDateFilters.receivedDateTo)
      : null;

    setTempDateFilters((prev) => ({
      ...prev,
      receivedDate: [from, to],
    }));
  }
}

  useEffect(() => {
    getStatusOptions().then((options) => setStatusOptions(options.map(opt => ({ text: opt.name, value: opt.id }))));
    getCaseTypeOptions().then((options) => setCaseTypeOptions(options.map(opt => ({ text: opt.name, value: opt.id }))));
    getClients().then((options) => setClientOptions(options.map(opt => ({ text: opt.companyName, value: opt.clientId }))));
  }, []);

  // ** Fetch Data from API **
  const loadData = async (params: SearchParams) => {
    setLoading(true);
    legalSearch(params, (response) => {
      if (response !== "error") {
        setData(response.items);
        setTotalRecords(response.totalRecords);
      }
      setLoading(false);
    });
  };

  // ** API Call when filters change **
  useEffect(() => {
    const params: SearchParams = {
      pageIndex: currentPage - 1,
      pageSize: pageSize,
      sortField: sortedInfo.column ? sortedInfo.field?.toString() || "" : "",
      sortDirection: sortedInfo.order ? (sortedInfo.order === "ascend" ? "ascend" : "descend") : "",
      receivedDateFrom: appliedDateFilters.receivedDateFrom,
      receivedDateTo: appliedDateFilters.receivedDateTo,
      incidentDateFrom: appliedDateFilters.incidentDateFrom,
      incidentDateTo: appliedDateFilters.incidentDateTo,
      searchText: searchText,
      searchType: searchTypeValue,
      statusId: statusFilter,
      caseTypeId: caseTypeFilter,
      clientIds: clientFilter,
    };

    loadData(params);
  }, [ appliedDateFilters, currentPage, pageSize, sortedInfo,clientFilter,caseTypeFilter,statusFilter,searchTypeValue]);

  // ** Handle Sorting & Table Events **
//   const handleTableChange = (pagination: TablePaginationConfig, _: any, sorter: SorterResult<any>,filters :any) => {
//     setSortedInfo(sorter);
//     setCurrentPage(pagination.current || 1);
//     setPageSize(pagination.pageSize || 10);
//     setClientFilter(filters.clientId);
//   };

  const handleTableChange: TableProps<any>['onChange'] = (pagination, filters, sorter, extra) => {
          setCurrentPage(pagination.current ?? 1);
          setPageSize(pagination.pageSize ?? 10);
          //setStatusFilter(filters.statusName);
          setClientFilter(filters?.client);
          setStatusFilter(filters?.status);
          setCaseTypeFilter(filters?.caseType);
          setSortedInfo(sorter as SorterResult<any>);
          console.log(sorter)
      };
  // ** Handle Date Selection Without Applying Immediately **
  const handleTempDateChange = (field: string, dates: any) => {
    setTempDateFilters((prev) => ({
      ...prev,
      [field]: dates || [null, null], // ✅ Fix: Ensures no null error
    }));
  };

  // ** Apply Date Filter on OK Click & Update Filter Icon **
  const applyDateFilter = (field: string, confirm: Function) => {
    const selectedDates = tempDateFilters[field];

    setAppliedDateFilters((prev) => ({
      ...prev,
      [`${field}From`]: selectedDates[0] ? dayjs(selectedDates[0]).format("YYYY-MM-DD") : null,
      [`${field}To`]: selectedDates[1] ? dayjs(selectedDates[1]).format("YYYY-MM-DD") : null,
    }));

    setActiveFilters((prev) => ({
      ...prev,
      [field]: selectedDates[0] !== null && selectedDates[1] !== null, // ✅ Fix: Properly handles cleared filters
    }));

    confirm({ closeDropdown: true });
  };


  const onChangeSearchType = (value: string) => {
    setSearchTypeValue(value);
  };

  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
    debouncedSearch();
  };
  function handleNavigation(id: string) {

      window.location.href = baseUrl + `/Reservation/Reservation/EditLegal?legalInfoId=${id}`; 
}

  // ** Table Columns with Sorting & Date Filters **
  const legalInfoColumns: TableColumnsType<any> = [
    { title: "Legal ID", dataIndex: "id", sorter: true,
        render: (value) => {
            return (
                <a
                    onClick={(event) => handleNavigation(value)}
                    target="_blank"
                >
                    {value}
                </a>
            );
        }
    },
    { title: "Member ID", dataIndex: "memberStringId", sorter: true },
    { title: "Member Name", dataIndex: "memberFullName", sorter: true },
    {
        title: "Date Received",
        dataIndex: "dateReceived",
        render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
        sorter: true,
        filtered: activeFilters.receivedDate || false,
        filterOnClose: false, // prevent default auto-clearing behavior
        onFilterDropdownOpenChange:(visible) => handleRecievedDateFilterOpen(visible), // new logic
        filterDropdown: ({ confirm }) => (
          <div style={{ padding: 8 }}>
            <RangePicker
              format="MM/DD/YYYY"
              onChange={(dates) => handleTempDateChange("receivedDate", dates)}
              value={tempDateFilters.receivedDate}
            />
            <Flex justify="flex-end" style={{ marginTop: 8 }}>
              <Button type="primary" size="small" onClick={() => applyDateFilter("receivedDate", confirm)}>
                OK
              </Button>
            </Flex>
          </div>
        ),
    },
    {
        title: "Client",
        dataIndex: "client",
        sorter: true,
        filters: clientOptions,
        filteredValue: clientFilter || null,
        filterOnClose: false
      },
    {
        title: "Case Type",
        dataIndex: "caseType",
        sorter: true,
        filters: caseTypeOptions,
        filteredValue: caseTypeFilter || null,
        filterOnClose: false
    },
    {
        title: "Date of Incident",
        dataIndex: "dateOfIncident",
        render: (text) => (text ? dayjs(text).format("MM/DD/YYYY") : ""),
        sorter: true,
        filtered: activeFilters.incidentDate || false,
        filterOnClose: false, // prevent default auto-clearing behavior
        onFilterDropdownOpenChange:(visible) => handleIncidentDateFilterOpen(visible), // new logic
        filterDropdown: ({ confirm }) => (
          <div style={{ padding: 8 }}>
            <RangePicker
              format="MM/DD/YYYY"
              onChange={(dates) => handleTempDateChange("incidentDate", dates)}
              value={tempDateFilters.incidentDate}
            />
            <Flex justify="flex-end" style={{ marginTop: 8 }}>
              <Button type="primary" size="small" onClick={() => applyDateFilter("incidentDate", confirm)}>
                OK
              </Button>
            </Flex>
          </div>
        ),
      },
      
    { title: "Claim Number", dataIndex: "claimNumber", sorter: true },
    { title: "Provider", dataIndex: "provider", sorter: true },
    {
        title: "Status",
        dataIndex: "status",
        sorter: true,
        filters: statusOptions,
        filteredValue : statusFilter || null,
        filterOnClose: false
    },
  ];

  return (
    <ConfigProvider theme={{ components: { Layout: { bodyBg: "#eff1f5", siderBg: "#fff" } } }}>
      <Layout style={{ borderWidth: 0, borderTopWidth: 1, borderColor: "#d9d9d9", borderStyle: "solid", height: "100%" }}>
        <Layout style={{ minHeight: "calc(100vh - 165px)", padding: "2rem" }}>
          <Content ref={listRef}>
            <Flex justify="end" style={{ marginBottom: "2rem" }} gap="small">
              <Input onChange={handleSearch} style={{ width: "35vw" }} allowClear addonBefore={<Select style={{ width: "120px" }} options={searchTypeOptions} onSelect={onChangeSearchType} value={searchTypeValue} />} />
            </Flex>
            <Table dataSource={data} columns={legalInfoColumns} rowKey="id" loading={loading} onChange={handleTableChange} pagination={{ current: currentPage, pageSize, total: totalRecords, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,showSizeChanger: true }} />
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
}




